import React from 'react';
import { PropertyGroup } from '../PropertyGroup';
import { PropertyColor } from '../property-types/PropertyColor';
import { PropertyBackgroundLayers } from '../property-types/PropertyBackgroundLayers';
import { PropertySelect } from '../property-types/PropertySelect';
import { PropertyNumber } from '../property-types/PropertyNumber';

interface BackgroundLayer {
  id: string;
  type: 'linear-gradient' | 'radial-gradient' | 'image';
  value: string;
}

interface BackgroundData {
  backgroundColor: string;
  backgroundImage: string;
  backgroundSize: string;
  backgroundPosition: string;
  backgroundRepeat: string;
  layers?: BackgroundLayer[];
}

interface BackgroundGroupProps {
  background: BackgroundData;
  onBackgroundChange: (property: keyof BackgroundData, value: string) => void;
}

const BACKGROUND_SIZE_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'cover', label: 'Cover' },
  { value: 'contain', label: 'Contain' },
  { value: '100% 100%', label: 'Stretch' },
];

const BACKGROUND_POSITION_OPTIONS = [
  { value: 'center', label: 'Center' },
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'top left', label: 'Top Left' },
  { value: 'top right', label: 'Top Right' },
  { value: 'bottom left', label: 'Bottom Left' },
  { value: 'bottom right', label: 'Bottom Right' },
];

const BACKGROUND_REPEAT_OPTIONS = [
  { value: 'no-repeat', label: 'No Repeat' },
  { value: 'repeat', label: 'Repeat' },
  { value: 'repeat-x', label: 'Repeat X' },
  { value: 'repeat-y', label: 'Repeat Y' },
];

export const BackgroundGroup: React.FC<BackgroundGroupProps> = ({
  background,
  onBackgroundChange
}) => {
  const layers = background.layers || [];

  const handleLayersChange = (newLayers: BackgroundLayer[]) => {
    // Pass layers to parent - parent will handle building the CSS string
    onBackgroundChange('layers' as any, newLayers as any);
  };

  // Parse background position (format: "0% 50%")
  const positionParts = background.backgroundPosition.split(' ');
  const posX = parseFloat(positionParts[0]) || 0;
  const posY = parseFloat(positionParts[1]) || 0;

  return (
    <PropertyGroup title="Background">
      {/* Background Color */}
      <div className="flex items-center gap-2 py-0.5 mt-1">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Color</span>
        <div className="flex flex-1 gap-2">
          <PropertyColor
            label=""
            value={background.backgroundColor}
            onChange={(value) => onBackgroundChange('backgroundColor', value)}
          />
        </div>
      </div>

      {/* Background Layers */}
      <div className="py-0.5 mt-2">
        <PropertyBackgroundLayers
          layers={layers}
          onChange={handleLayersChange}
        />
      </div>

      {/* Background Size X and Y */}
      <div className="flex items-center gap-2 py-0.5 mt-2">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Size</span>
        <div className="flex gap-2 flex-1">
          <PropertyNumber
            label="X"
            value={parseFloat(background.backgroundSize.split(' ')[0]) || 100}
            onChange={(value) => {
              const parts = background.backgroundSize.split(' ');
              const y = parts[1] || '100%';
              onBackgroundChange('backgroundSize', `${value}% ${y}`);
            }}
            step={1}
            suffix="%"
          />
          <PropertyNumber
            label="Y"
            value={parseFloat(background.backgroundSize.split(' ')[1]) || 100}
            onChange={(value) => {
              const parts = background.backgroundSize.split(' ');
              const x = parts[0] || '100%';
              onBackgroundChange('backgroundSize', `${x} ${value}%`);
            }}
            step={1}
            suffix="%"
          />
        </div>
      </div>

      {/* Background Position X and Y */}
      <div className="flex items-center gap-2 py-0.5">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Position</span>
        <div className="flex gap-2 flex-1">
          <PropertyNumber
            label="X"
            value={posX}
            onChange={(value) => {
              onBackgroundChange('backgroundPosition', `${value}% ${posY}%`);
            }}
            step={1}
            suffix="%"
          />
          <PropertyNumber
            label="Y"
            value={posY}
            onChange={(value) => {
              onBackgroundChange('backgroundPosition', `${posX}% ${value}%`);
            }}
            step={1}
            suffix="%"
          />
        </div>
      </div>

      {/* Background Repeat */}
      <div className="flex items-center gap-2 py-0.5">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Repeat</span>
        <div className="flex flex-1 gap-2">
          <PropertySelect
            label=""
            value={background.backgroundRepeat}
            options={BACKGROUND_REPEAT_OPTIONS}
            onChange={(value) => onBackgroundChange('backgroundRepeat', value)}
          />
        </div>
      </div>
    </PropertyGroup>
  );
};

